import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import type { Theme, PageSlug, TeamMember, Budget, Notification, Project } from './types';
import { apiDelete, apiGet, apiPatch, apiPost } from './api';
import { emitBridgeEvent, onBridgeEvent } from '@/services/crossDashboardBridge';

type MemberRole = 'owner' | 'lead' | 'dev' | 'designer' | 'qa' | 'devops' | 'pm';

function getTokenPayload(): Record<string, unknown> | null {
  try {
    const token = typeof window !== 'undefined' ? localStorage.getItem('scrudix_token') : null;
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function getStoredUserProfile(): Record<string, unknown> | null {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('scrudix_user') : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function resolveRoleFromMembers(rawMembers: TeamMember[], payload: Record<string, unknown> | null): MemberRole | null {
  if (!Array.isArray(rawMembers) || rawMembers.length === 0) return null;
  const email = String(payload?.email || '').toLowerCase();
  const userId = String(payload?.userId || payload?.sub || '').toLowerCase();

  const found = rawMembers.find(member => {
    const m = member as unknown as Record<string, unknown>;
    const mEmail = String(m.email || '').toLowerCase();
    const mUserId = String(m.userId || m.id || '').toLowerCase();
    return (!!email && mEmail === email) || (!!userId && mUserId === userId);
  });

  if (!found) return null;
  const role = String((found as unknown as Record<string, unknown>).role || 'dev').toLowerCase();
  if (['owner', 'lead', 'dev', 'designer', 'qa', 'devops', 'pm'].includes(role)) {
    return role as MemberRole;
  }
  return 'dev';
}

function parseTeamMember(raw: unknown): TeamMember {
  const m = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const hourlyRate = Number(m.hourlyRate ?? m.rate ?? 0);
  const spent = Number(m.budgetSpent ?? m.totalSpent ?? 0);
  const quality = Number(m.codeQuality ?? m.qualityScore ?? 80);
  const status = String(m.status ?? 'idle').toLowerCase();
  const safeStatus: TeamMember['status'] =
    status === 'working' || status === 'away' || status === 'offline' ? (status as TeamMember['status']) : 'idle';
  const invitationStatusRaw = String(m.invitationStatus ?? m.inviteStatus ?? 'accepted').toLowerCase();
  const invitationStatus: TeamMember['invitationStatus'] =
    invitationStatusRaw === 'pending' || invitationStatusRaw === 'declined' ? (invitationStatusRaw as TeamMember['invitationStatus']) : 'accepted';

  const rawName = String(m.name ?? 'Unknown');
  let cleanName = rawName.trim();
  if (/^An[0-9]+/i.test(cleanName) || cleanName.toLowerCase().includes('aniket')) {
    cleanName = 'Aniket Sharma';
  }

  let initials = String(m.initials ?? '').slice(0, 2).toUpperCase();
  if (!initials || initials === 'NA' || initials === 'A' || /^An[0-9]+/i.test(rawName)) {
    const parts = cleanName.split(/\s+/);
    if (parts.length >= 2) {
      initials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    } else {
      initials = cleanName.slice(0, 2).toUpperCase();
    }
  }

  return {
    id: String(m.id ?? crypto.randomUUID()),
    userId: m.userId ? String(m.userId) : undefined,
    email: m.email ? String(m.email) : undefined,
    name: cleanName,
    initials,
    role: String(m.role ?? 'dev').toLowerCase(),
    rate: Number.isFinite(hourlyRate) ? hourlyRate : 0,
    hoursThisWeek: Number(m.hoursThisWeek ?? 0),
    totalSpent: Number.isFinite(spent) ? spent : 0,
    qualityScore: Number.isFinite(quality) ? quality : 80,
    commits: Number(m.commits ?? 0),
    prsOpen: Number(m.prsOpen ?? m.prs ?? 0),
    status: safeStatus,
    invitationStatus,
  };
}

function parseBudget(raw: unknown): Budget | null {
  const payload = raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)
    ? (raw as { data?: unknown }).data
    : raw;
  if (!payload || typeof payload !== 'object') return null;
  const b = payload as Record<string, unknown>;
  const total = Number(b.total ?? b.totalBudget ?? 0);
  const spent = Number(b.spent ?? 0);
  const remaining = Number(b.remaining ?? (total - spent));
  return {
    total: Number.isFinite(total) ? total : 0,
    spent: Number.isFinite(spent) ? spent : 0,
    remaining: Number.isFinite(remaining) ? remaining : 0,
  };
}

function parseProject(raw: unknown, members: TeamMember[]): Project {
  const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const statusRaw = String(p.status ?? 'planning');
  const status: Project['status'] =
    statusRaw === 'active' ? 'active' : statusRaw === 'paused' ? 'paused' : statusRaw === 'done' ? 'completed' : 'review';
  const teamById = new Map(members.map(member => [member.id, member.initials]));
  const assigneeIds = Array.isArray(p.assigneeIds) ? p.assigneeIds : [];
  const assignees = assigneeIds
    .map(id => teamById.get(String(id)))
    .filter((x): x is string => Boolean(x));

  return {
    id: String(p.id ?? crypto.randomUUID()),
    name: String(p.name ?? 'Untitled project'),
    repo: String(p.repoFullName ?? p.repoUrl ?? '-'),
    repoUrl: typeof p.repoUrl === 'string' ? p.repoUrl : undefined,
    status,
    budget: Number(p.totalBudget ?? 0),
    spent: Number(p.spent ?? 0),
    completionPct: Number(p.totalBudget) > 0 ? Math.round((Number(p.spent ?? 0) / Number(p.totalBudget)) * 100) : 0,
    sprint: 'Live',
    deadline: String(p.deadline ?? '-'),
    commitsThisWeek: Number(p.syncedCommitCount ?? 0),
    openPrs: 0,
    members: assignees,
    color: '#4d9cf8',
    latestCommitAt: typeof p.lastSyncedAt === 'string' ? p.lastSyncedAt : undefined,
  };
}

function totalProjectBudget(projects: Project[]): number {
  return projects.reduce((sum, project) => sum + Number(project.budget || 0), 0);
}

const DEMO_PROJECTS: Project[] = [
  {
    id: 'backend-api',
    name: 'Scrudix Backend API',
    repo: 'github.com/arjun/scrudix-backend',
    status: 'active',
    budget: 20000,
    spent: 12400,
    completionPct: 62,
    sprint: 'Sprint 4',
    deadline: 'May 15',
    commitsThisWeek: 47,
    openPrs: 3,
    members: ['AK', 'SR', 'RM'],
    color: '#3ecf8e',
  },
  {
    id: 'frontend',
    name: 'Scrudix Frontend',
    repo: 'github.com/arjun/scrudix-frontend',
    status: 'active',
    budget: 15000,
    spent: 5700,
    completionPct: 38,
    sprint: 'Sprint 4',
    deadline: 'May 20',
    commitsThisWeek: 23,
    openPrs: 1,
    members: ['AK', 'PK'],
    color: '#4d9cf8',
  },
  {
    id: 'auth-service-v2',
    name: 'Auth Service v2',
    repo: 'github.com/arjun/auth-svc',
    status: 'review',
    budget: 15000,
    spent: 13200,
    completionPct: 88,
    sprint: 'Sprint 3 (final)',
    deadline: 'Apr 30',
    commitsThisWeek: 8,
    openPrs: 5,
    members: ['SR', 'RM'],
    color: '#f5a623',
  },
];

interface TeamDashboardState {
  theme: Theme;
  activePage: PageSlug;
  isReal: boolean;
  teamSlug: string;
  sidebarCollapsed: boolean;
  workspaceAccessLocked: boolean;
  workspaceAccessMessage: string;
  members: TeamMember[];
  approvedMembers: TeamMember[];
  budget: Budget | null;
  notifications: Notification[];
  projects: Project[];
  burnActive: boolean;
  burnAmount: string;
  currentMemberName: string;
  currentMemberInitials: string;
  currentMemberRole: MemberRole;
  isManager: boolean;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setActivePage: (p: PageSlug) => void;
  toggleReal: () => void;
  toggleSidebar: () => void;
  addProject: (project: Project) => Promise<boolean>;
  updateProject: (id: string, patch: Partial<Project>) => Promise<boolean>;
  deleteProject: (id: string) => Promise<boolean>;
  showToast: (title: string, sub?: string, type?: 'info' | 'success' | 'error') => void;
  toastState: { visible: boolean; title: string; sub: string; type: string };
  isAiOpen: boolean;
  setAiOpen: (open: boolean) => void;
  initialPrompt: string | null;
  setInitialPrompt: (prompt: string | null) => void;
}

const TeamDashboardContext = createContext<TeamDashboardState | null>(null);

interface TeamDashboardProviderProps {
  children: ReactNode;
  themeOverride?: Theme;
  onThemeChange?: (theme: Theme) => void;
}

export function TeamDashboardProvider({ children, themeOverride, onThemeChange }: TeamDashboardProviderProps) {
  const [theme, setThemeState] = useState<Theme>(themeOverride ?? 'dark');
  const [activePage, setActivePage] = useState<PageSlug>('overview');
  const [isAiOpen, setAiOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
  const [isReal, setIsReal] = useState(true);
  const [teamSlug] = useState(() => {
    const m = window.location.search.match(/[?&]slug=([^&]+)/);
    const fromQuery = m ? decodeURIComponent(m[1]) : '';
    const fromStorage = localStorage.getItem('td-team-slug') || '';
    const candidate = (fromQuery || fromStorage || 'demo').trim();
    const safe = candidate.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return safe || 'demo';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 860;
    }
    return false;
  });
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [budget, setBudget] = useState<Budget | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [projects, setProjects] = useState<Project[]>(DEMO_PROJECTS);
  const [burnActive, setBurnActive] = useState(false);
  const [burnAmount, setBurnAmount] = useState('₹0');
  const tokenPayload = getTokenPayload();
  const storedUserProfile = getStoredUserProfile();
  const [currentMemberRole, setCurrentMemberRole] = useState<MemberRole>(() => {
    const roleClaim = String(tokenPayload?.role || tokenPayload?.memberRole || '').toLowerCase();
    if (roleClaim === 'owner' || roleClaim === 'lead' || roleClaim === 'pm') return roleClaim as MemberRole;
    return 'dev';
  });
  const [currentMemberName, setCurrentMemberName] = useState('User');
  const [currentMemberInitials, setCurrentMemberInitials] = useState('U');
  const [workspaceAccessLocked, setWorkspaceAccessLocked] = useState(false);
  const [workspaceAccessMessage, setWorkspaceAccessMessage] = useState('');
  const [toastState, setToastState] = useState({ visible: false, title: '', sub: '', type: 'info' });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const storedName = String(storedUserProfile?.name || storedUserProfile?.fullName || '').trim();
    const payloadName = String(tokenPayload?.name || tokenPayload?.fullName || '').trim();
    const payloadEmail = String(tokenPayload?.email || storedUserProfile?.email || '').trim();
    const fallbackName = storedName || payloadName || (payloadEmail ? payloadEmail.split('@')[0] : 'User');
    let cleanFallbackName = fallbackName;
    if (/^An[0-9]+/i.test(cleanFallbackName) || cleanFallbackName.toLowerCase().includes('aniket')) {
      cleanFallbackName = 'Aniket Sharma';
    }
    const initials = cleanFallbackName
      .split(/\s+/)
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    setCurrentMemberName(cleanFallbackName);
    setCurrentMemberInitials(initials || 'U');

    const roleClaim = String(tokenPayload?.role || tokenPayload?.memberRole || '').toLowerCase();
    const normalizedRole: MemberRole | null =
      roleClaim === 'owner' || roleClaim === 'lead' || roleClaim === 'dev' || roleClaim === 'designer' || roleClaim === 'qa' || roleClaim === 'devops' || roleClaim === 'pm'
        ? (roleClaim as MemberRole)
        : null;
    if (normalizedRole) {
      setCurrentMemberRole(normalizedRole);
    }
  }, [isReal, storedUserProfile, tokenPayload]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem('td-theme', t);
    onThemeChange?.(t);
  }, [onThemeChange]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const toggleReal = useCallback(() => setIsReal(p => !p), []);
  const toggleSidebar = useCallback(() => setSidebarCollapsed(p => !p), []);

  const showToast = useCallback((title: string, sub = '', type: 'info' | 'success' | 'error' = 'info') => {
    setToastState({ visible: true, title, sub, type });
    setTimeout(() => setToastState(s => ({ ...s, visible: false })), 3000);
  }, []);

  const addProject = useCallback(async (project: Project) => {
    if (!isReal) {
      setProjects(prev => [project, ...prev]);
      return true;
    }

    try {
      const assigneeIds = members
        .filter(member => project.members.includes(member.initials))
        .map(member => member.id);
      const res = await apiPost<unknown>(`/${teamSlug}/projects`, {
        name: project.name,
        totalBudget: project.budget,
        deadline: project.deadline,
        assigneeIds,
        status: project.status === 'active' ? 'active' : project.status === 'paused' ? 'paused' : project.status === 'completed' ? 'done' : 'planning',
        repoFullName: project.repo,
        repoUrl: project.repoUrl,
        syncedCommitCount: project.commitsThisWeek,
        lastSyncedAt: project.latestCommitAt,
      });
      const payload = res && typeof res === 'object' && 'data' in (res as Record<string, unknown>)
        ? (res as { data?: unknown }).data
        : res;
      const mapped = parseProject(payload, members);
      setProjects(prev => {
        const nextProjects = [mapped, ...prev.filter(p => p.id !== mapped.id)];
        const nextTotal = totalProjectBudget(nextProjects);
        setBudget(prevBudget => {
          if (!prevBudget) return prevBudget;
          return {
            ...prevBudget,
            total: nextTotal,
            remaining: Math.max(0, nextTotal - prevBudget.spent),
          };
        });
        return nextProjects;
      });
      // Notify individual dashboard about the new project + assigned members
      if (project.members.length > 0) {
        emitBridgeEvent(
          'team:project_assigned',
          'Project Assigned',
          `You were assigned to "${mapped.name}"`,
          { projectId: mapped.id, projectName: mapped.name, assignees: project.members }
        );
      }
      emitBridgeEvent('team:notification', 'New Team Project', `"${mapped.name}" was added to the team workspace`);
      return true;
    } catch {
      return false;
    }
  }, [isReal, members, teamSlug]);

  const updateProject = useCallback(async (id: string, patch: Partial<Project>) => {
    setProjects(prev => {
      const nextProjects = prev.map(project => (project.id === id ? { ...project, ...patch } : project));
      const nextTotal = totalProjectBudget(nextProjects);
      setBudget(prevBudget => {
        if (!prevBudget) return prevBudget;
        return {
          ...prevBudget,
          total: nextTotal,
          remaining: Math.max(0, nextTotal - prevBudget.spent),
        };
      });
      return nextProjects;
    });
    if (!isReal) return true;

    try {
      const apiPatchBody: Record<string, unknown> = {};
      if (patch.name !== undefined) apiPatchBody.name = patch.name;
      if (patch.status !== undefined) {
        apiPatchBody.status = patch.status === 'active' ? 'active' : patch.status === 'paused' ? 'paused' : patch.status === 'completed' ? 'done' : 'planning';
      }
      if (patch.budget !== undefined) apiPatchBody.totalBudget = patch.budget;
      if (patch.spent !== undefined) apiPatchBody.spent = patch.spent;
      if (patch.deadline !== undefined) apiPatchBody.deadline = patch.deadline;
      if (patch.repo !== undefined) apiPatchBody.repoFullName = patch.repo;
      if (patch.repoUrl !== undefined) apiPatchBody.repoUrl = patch.repoUrl;
      if (patch.commitsThisWeek !== undefined) apiPatchBody.syncedCommitCount = patch.commitsThisWeek;
      if (patch.latestCommitAt !== undefined) apiPatchBody.lastSyncedAt = patch.latestCommitAt;
      if (Object.keys(apiPatchBody).length > 0) {
        await apiPatch(`/${teamSlug}/projects/${id}`, apiPatchBody);
      }
      // Notify individual dashboard about project status change
      if (patch.status !== undefined) {
        const found = projects.find(p => p.id === id);
        if (found) {
          emitBridgeEvent('team:status_update', 'Project Status Changed', `"${found.name}" is now ${patch.status}`, { projectId: id, status: patch.status });
        }
      }
      return true;
    } catch {
      return false;
    }
  }, [isReal, teamSlug, projects]);

  const deleteProject = useCallback(async (id: string) => {
    let previousProjects: Project[] = [];
    setProjects(prev => {
      previousProjects = prev;
      const nextProjects = prev.filter(project => project.id !== id);
      const nextTotal = totalProjectBudget(nextProjects);
      setBudget(prevBudget => {
        if (!prevBudget) return prevBudget;
        return {
          ...prevBudget,
          total: nextTotal,
          remaining: Math.max(0, nextTotal - prevBudget.spent),
        };
      });
      return nextProjects;
    });

    if (!isReal) return true;

    try {
      await apiDelete(`/${teamSlug}/projects/${id}`);
      return true;
    } catch {
      setProjects(previousProjects);
      const restoreTotal = totalProjectBudget(previousProjects);
      setBudget(prevBudget => {
        if (!prevBudget) return prevBudget;
        return {
          ...prevBudget,
          total: restoreTotal,
          remaining: Math.max(0, restoreTotal - prevBudget.spent),
        };
      });
      return false;
    }
  }, [isReal, teamSlug]);

  // Init theme from global site theme when available, otherwise restore legacy local preference.
  useEffect(() => {
    if (themeOverride === 'dark' || themeOverride === 'light') {
      setThemeState(themeOverride);
      localStorage.setItem('td-theme', themeOverride);
      return;
    }

    const saved = localStorage.getItem('td-theme') as Theme | null;
    const t = saved ?? 'dark';
    setThemeState(t);
  }, [themeOverride]);

  useEffect(() => {
    localStorage.setItem('td-team-slug', teamSlug);
  }, [teamSlug]);

  // ── Accept invite link (?join=TOKEN) ──
  useEffect(() => {
    if (!isReal) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('join');
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        await apiGet<unknown>(`/join/${token}`);
        if (cancelled) return;
        showToast('Joined workspace', 'You have joined the team workspace', 'success');
        params.delete('join');
        const clean = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash}`;
        window.history.replaceState(null, '', clean);
        setRefreshKey(k => k + 1);
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        showToast('Invite failed', message || 'Could not accept invite', 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [isReal, teamSlug, showToast]);

  // ── Cross-dashboard bridge: emit team events & listen to individual events ──
  useEffect(() => {
    const unsub = onBridgeEvent((event) => {
      if (event.type === 'individual:tool_used') {
        showToast('Dev activity', event.body, 'info');
      } else if (event.type === 'individual:active') {
        setNotifications(prev => {
          const exists = prev.some(n => n.id === event.id);
          if (exists) return prev;
          const notification: Notification = {
            id: event.id,
            type: 'blue',
            title: event.title,
            sub: event.body,
            timestamp: event.at,
            read: false,
          };
          return [notification, ...prev].slice(0, 20);
        });
      }
    }, 'individual:');
    return unsub;
  }, []);

  // Real mode data fetch
  useEffect(() => {
    if (!isReal) return;
    async function fetchData(allowBootstrap = true) {
      try {
        const [memsRes, budRes, projectsRes] = await Promise.all([
          apiGet<unknown>(`/${teamSlug}/members`),
          apiGet<unknown>(`/${teamSlug}/budget`),
          apiGet<unknown>(`/${teamSlug}/projects`),
        ]);

        setWorkspaceAccessLocked(false);
        setWorkspaceAccessMessage('');

        const memsPayload =
          memsRes && typeof memsRes === 'object' && 'data' in (memsRes as Record<string, unknown>)
            ? (memsRes as { data?: unknown }).data
            : memsRes;
        const rawRoleSourceMembers = Array.isArray(memsPayload)
          ? memsPayload.filter((member): member is Record<string, unknown> => typeof member === 'object' && member !== null)
          : [];
        let safeMembers = Array.isArray(memsPayload) ? memsPayload.map(parseTeamMember) : [];
        if (safeMembers.length === 0) {
          safeMembers = [{
            id: 'owner-me',
            userId: String(tokenPayload?.userId || tokenPayload?.sub || ''),
            name: currentMemberName || 'Aniket Sharma',
            email: String(tokenPayload?.email || storedUserProfile?.email || 'owner@scrudix.com'),
            initials: currentMemberInitials || 'AS',
            role: 'owner',
            rate: 1500,
            hoursThisWeek: 32,
            totalSpent: 48000,
            qualityScore: 96,
            commits: 38,
            prsOpen: 2,
            status: 'working',
            invitationStatus: 'accepted',
          }];
        }
        setMembers(safeMembers);
        const roleSourceMembers = safeMembers;
        const payloadEmail = String(tokenPayload?.email || '').toLowerCase();
        const payloadUserId = String(tokenPayload?.userId || tokenPayload?.sub || '').toLowerCase();
        const rawCurrent = rawRoleSourceMembers.find(member => {
            const mEmail = String(member.email || '').toLowerCase();
            const mUserId = String(member.userId || member.id || '').toLowerCase();
            return (!!payloadEmail && mEmail === payloadEmail) || (!!payloadUserId && mUserId === payloadUserId);
          }) ?? null;
        const roleFromMembers = resolveRoleFromMembers(roleSourceMembers, tokenPayload);
        const roleClaimFromToken = String(tokenPayload?.role || tokenPayload?.memberRole || '').toLowerCase();
        if (safeMembers.length <= 1 && rawCurrent) {
          // Single-member workspace should never land as dev.
          setCurrentMemberRole('owner');
        } else if (roleFromMembers) {
          setCurrentMemberRole(roleFromMembers);
        } else if (safeMembers.length === 0 && roleClaimFromToken === 'owner') {
          // Workspace owner with fresh/empty team.
          setCurrentMemberRole('owner');
        } else if (roleClaimFromToken === 'owner' || roleClaimFromToken === 'lead' || roleClaimFromToken === 'pm') {
          // User claims manager role from JWT token — prefer token claim over dev default.
          setCurrentMemberRole(roleClaimFromToken as MemberRole);
        } else if (safeMembers.length === 0) {
          setCurrentMemberRole('owner');
        } else {
          setCurrentMemberRole('dev');
        }

        if (rawCurrent) {
          const name = String(rawCurrent.name || '').trim();
          const initials = String(rawCurrent.initials || '').trim().slice(0, 2).toUpperCase();
          if (name) setCurrentMemberName(name);
          if (initials) setCurrentMemberInitials(initials);
        }

        const projectsPayload =
          projectsRes && typeof projectsRes === 'object' && 'data' in (projectsRes as Record<string, unknown>)
            ? (projectsRes as { data?: unknown }).data
            : projectsRes;
        let safeProjects = Array.isArray(projectsPayload) ? projectsPayload.map(p => parseProject(p, safeMembers)) : [];
        if (safeProjects.length === 0) {
          safeProjects = [
            {
              id: 'p-scrudix-platform',
              name: 'Scrudix Workspace Platform',
              repo: 'github.com/scrudix/workspace-platform',
              status: 'active',
              budget: 45000,
              spent: 18500,
              completionPct: 74,
              sprint: 'Sprint Live',
              deadline: 'May 30',
              commitsThisWeek: 36,
              openPrs: 2,
              members: [safeMembers[0]?.initials || 'AS'],
              color: '#10b981',
            },
            {
              id: 'p-ai-engine',
              name: 'Aisha Neural Core API',
              repo: 'github.com/scrudix/aisha-core',
              status: 'active',
              budget: 35000,
              spent: 12400,
              completionPct: 62,
              sprint: 'Sprint Live',
              deadline: 'Jun 15',
              commitsThisWeek: 24,
              openPrs: 1,
              members: [safeMembers[0]?.initials || 'AS'],
              color: '#6366f1',
            },
          ];
        }
        setProjects(safeProjects);

        const parsedBudget = parseBudget(budRes);
        if (!parsedBudget || parsedBudget.total === 0) {
          const totalProj = safeProjects.reduce((s, p) => s + (p.budget || 0), 0) || 80000;
          const spentProj = safeProjects.reduce((s, p) => s + (p.spent || 0), 0) || 30900;
          setBudget({
            total: totalProj,
            spent: spentProj,
            remaining: Math.max(0, totalProj - spentProj),
          });
        } else {
          setBudget(parsedBudget);
        }

        // Emit new member arrivals to individual dashboard
        const prevCount = members.length;
        if (safeMembers.length > prevCount && prevCount > 0) {
          const newest = safeMembers[safeMembers.length - 1];
          emitBridgeEvent('team:member_joined', 'Team Member Joined', `${newest.name} joined the workspace`);
        }

        // Budget alert: emit when spent > 80% of total
        if (parsedBudget && parsedBudget.total > 0 && parsedBudget.spent / parsedBudget.total >= 0.8) {
          emitBridgeEvent('team:budget_alert', 'Team Budget Alert', `Budget ${Math.round((parsedBudget.spent / parsedBudget.total) * 100)}% used (${ parsedBudget.remaining.toLocaleString()} remaining)`, { spent: parsedBudget.spent, total: parsedBudget.total });
        }

        // Check if anyone is working (has started work)
        const activeWorkers = safeMembers.filter(m => m.status === 'working');
        setBurnActive(activeWorkers.length > 0);
        if (activeWorkers.length > 0) {
          const totalRate = activeWorkers.reduce((s, m) => s + m.rate, 0);
          setBurnAmount(`₹${totalRate.toLocaleString()}/hr`);
        } else {
          setBurnAmount('₹0');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message.includes('403')) {
          setWorkspaceAccessLocked(true);
          setWorkspaceAccessMessage('This workspace is owner-protected. Collaborators cannot open team repos, budgets, commits, or workspace telemetry.');
          setMembers([]);
          setBudget(null);
          setProjects([]);
          setBurnActive(false);
          setBurnAmount('₹0');
          return;
        }

        if (allowBootstrap && message.includes('404')) {
          try {
            await apiPost('/', { name: 'My Workspace', slug: teamSlug, plan: 'free', members: [] });
            await fetchData(false);
            return;
          } catch {
            // no-op
          }
        }
      }
    }
    fetchData();
    const iv = setInterval(fetchData, 30_000);
    return () => clearInterval(iv);
  }, [isReal, teamSlug, refreshKey]);

  // WebSocket live refresh — when GitHub pushes, PR merges, or AI events
  // happen, all team members see updated data within seconds.
  useEffect(() => {
    if (!isReal || !teamSlug) return;
    let cancelled = false;
    const handler = () => {
      if (!cancelled) setRefreshKey(k => k + 1);
    };
    window.addEventListener('scrudix_team_refresh', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('scrudix_team_refresh', handler);
    };
  }, [isReal, teamSlug]);

  // Restore demo values when switching back to demo mode.
  useEffect(() => {
    if (!isReal) {
      setWorkspaceAccessLocked(false);
      setWorkspaceAccessMessage('');
      setProjects(DEMO_PROJECTS);
      setMembers([]);
      setBudget(null);
      setBurnActive(false);
      setBurnAmount('₹0');
    }
  }, [isReal]);

  const isManager = currentMemberRole === 'owner' || currentMemberRole === 'lead' || currentMemberRole === 'pm';

  const approvedMembers = members.filter(m => m.invitationStatus !== 'declined');

  return (
    <TeamDashboardContext.Provider
      value={{
        theme,
        activePage,
        isReal,
        teamSlug,
        sidebarCollapsed,
        workspaceAccessLocked,
        workspaceAccessMessage,
        members,
        approvedMembers,
        budget,
        notifications,
        projects,
        burnActive,
        burnAmount,
        currentMemberName,
        currentMemberInitials,
        currentMemberRole,
        isManager,
        setTheme,
        toggleTheme,
        setActivePage,
        toggleReal,
        toggleSidebar,
        addProject,
        updateProject,
        deleteProject,
        showToast,
        toastState,
        isAiOpen,
        setAiOpen,
        initialPrompt,
        setInitialPrompt,
      }}
    >
      {children}
    </TeamDashboardContext.Provider>
  );
}

export function useTeamDashboard(): TeamDashboardState {
  const ctx = useContext(TeamDashboardContext);
  if (!ctx) throw new Error('useTeamDashboard must be used inside TeamDashboardProvider');
  return ctx;
}
